import { Survey, SurveyState } from '@brainfocus/web/components/survey/survey';
import { SurveyQuestion, SurveyResult } from '@brainfocus/web/domain/survey/survey_format';
import { TextInput } from '@brainfocus/web/components/text_input/text_input';
import { useState } from 'preact/hooks';
import './signup.scss';
import { CsrfToken } from '@brainfocus/web/components/csrfToken/csfdToken';
import { useAsync } from '@brainfocus/web/utils/react/use_async';
import { BrainFocusApi, SignupPayload } from '@brainfocus/web/brainfocus_api';
import { assertUser, assertValue } from '@brainfocus/web/utils/assert';
import { flashError } from '@brainfocus/web/flash';

interface FieldOfWorkOtherSelection {
    selected: boolean;
    text: string;
}

function getFieldOfWorkOtherSelection(survey: SurveyQuestion, answers: SurveyResult): FieldOfWorkOtherSelection {
    if (!Array.isArray(survey.answers)) {
        return { selected: false, text: '' };
    }
    const topIndex = answers['q_0'];
    if (typeof topIndex !== 'number') {
        return { selected: false, text: '' };
    }
    const topAnswer = survey.answers[topIndex];
    if (!topAnswer || topAnswer.text !== 'Industry player' || !topAnswer.questions?.length) {
        return { selected: false, text: '' };
    }
    const fieldOfWorkQuestion = topAnswer.questions[0];
    if (!fieldOfWorkQuestion || !('answers' in fieldOfWorkQuestion) || !Array.isArray(fieldOfWorkQuestion.answers)) {
        return { selected: false, text: '' };
    }
    const fowKey = `q_0_${topIndex}_0`;
    const fowIndex = answers[fowKey];
    if (typeof fowIndex !== 'number') {
        return { selected: false, text: '' };
    }
    const fowAnswer = fieldOfWorkQuestion.answers[fowIndex];
    if (!fowAnswer || fowAnswer.text !== 'Other...') {
        return { selected: false, text: '' };
    }
    const freeTextKey = `q_0_${topIndex}_0_${fowIndex}_0`;
    const freeText = answers[freeTextKey];
    return {
        selected: true,
        text: typeof freeText === 'string' ? freeText : '',
    };
}

export function Signup() {
    const [surveyState, setSurveyState] = useState<SurveyState>();
    const { data: survey } = useAsync(() => BrainFocusApi.getSurvey().then(v => [v]), []);
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [institution, setInstitution] = useState<string>();
    const [position, setPosition] = useState<string>();
    const [country, setCountry] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [passwordConfirm, setPasswordConfirm] = useState<string>();
    const [tocAccepted, setTocAccepted] = useState<boolean>(false);
    const [truthfulnessConfirmed, setTruthfulnessConfirmed] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [signupComplete, setSignupComplete] = useState<boolean>(false);
    const [ndaPending, setNdaPending] = useState<boolean>(false);

    const fieldOfWorkOther = survey && surveyState
        ? getFieldOfWorkOtherSelection(survey[0]!, surveyState.answers)
        : { selected: false, text: '' };
    const otherSelectedButEmpty = fieldOfWorkOther.selected && fieldOfWorkOther.text.trim() === '';
    const submitDisabled = sending || !tocAccepted || !truthfulnessConfirmed || otherSelectedButEmpty;

    if (signupComplete) {
        // Neither branch offers a way into the platform (#869): no login link and
        // no login URL. An industry player has no access to give yet — the team
        // reaches out — so that branch skips the desktop reminder, which arrives
        // with the login link in the activation email.
        return (
            <div class='bf-ui-content bf-signup-confirmation'>
                {ndaPending
                    ? (
                        <>
                            <h1>Thank you for registering to BrainFocus</h1>
                            <p class='mb-2'>Because you indicated that you are an industry player, access to the platform is gated until a bilateral Non-Disclosure Agreement (NDA) between BrainFocus and your organisation has been verified by our team.</p>
                            <p class='mb-4'>A member of our team will contact you soon via e-mail.</p>
                        </>
                        )
                    : (
                        <>
                            <h1>Account created!</h1>
                            <p class='mb-2'>Check your e-mail to access the trial platform.</p>
                            <p class='mb-4'><em><strong>BrainFocus is designed for desktop.</strong> To continue, open BrainFocus on a desktop or laptop computer — the platform is not intended for use on mobile phones.</em></p>
                        </>
                        )}
            </div>
        );
    } else if (!survey) {
        return <div bf-loading></div>;
    } else {
        return (
            <div class='bf-ui-content p-0'><div class='bf-singup-ui'>
                <div class='bf-signup-ui-intro'>
                    <div class='container w-article'>
                        <div class='bf-signup-ui-intro-section'>
                            <p>
                                Welcome to your <strong>BrainFocus</strong> platform trial (SEEG analysis + MRI/CT integration for pre-surgical evaluation of drug-resistant epilepsy).
                            </p>
                            <p>Please answer this brief questionnaire before you begin.</p>
                        </div>
                        <div class='bf-signup-ui-intro-section bf-signup-ui-intro-section-image' style='background-image: url("/static/images/signup_intro.png")'>
                        </div>
                    </div>
                </div>
                <form method='POST' class='bf-form d-block container w-article pt-3' onSubmit={submitSignup}>
                    <Survey survey={survey} onUpdate={setSurveyState}></Survey>
                    {surveyState?.completed && (
                        <>
                            <h3 class='mt-2'>Account details</h3>
                            <div class='bf-form-row-2'>
                                <TextInput<string> type='text' value={firstName} onChange={setFirstName} name='first_name' autocomplete='given-name' label='First name' output='string' required></TextInput>
                                <TextInput<string> type='text' value={lastName} onChange={setLastName} name='last_name' autocomplete='family-name' label='Last name' output='string' required></TextInput>
                            </div>
                            <TextInput<string> type='email' autocomplete='email' value={email} onChange={setEmail} name='email' label='E-mail' output='string' required></TextInput>
                            <TextInput<string> type='text' value={institution} onChange={setInstitution} name='institution' label='Company or institution name' output='string' required></TextInput>
                            <TextInput<string> type='text' value={position} onChange={setPosition} name='position' label='Job position' output='string' required></TextInput>
                            <TextInput<string> type='text' value={country} onChange={setCountry} name='country' autocomplete='country-name' label='Country of residence' output='string' required></TextInput>
                            <h3 class='mt-2'>Create Password</h3>
                            <TextInput<string> type='password' value={password} onChange={setPassword} name='password' autocomplete='new-password' label='Password' output='string' required></TextInput>
                            <TextInput<string> type='password' value={passwordConfirm} onChange={setPasswordConfirm} name='password_confirm' autocomplete='new-password' label='Confirm Password' output='string' required></TextInput>
                            <label class='mb-2 d-flex align-items-start gap-2'>
                                <input type='checkbox' onChange={e => setTocAccepted((e.target as HTMLInputElement).checked)} checked={tocAccepted} name='toc_accepted' />
                                <span>I accept the <a href='/terms-of-service' target='_blank'>Terms and Conditions</a> and the <a href='/privacy-policy' target='_blank'>Privacy Policy</a>.</span>
                            </label>
                            <label class='mb-2 d-flex align-items-start gap-2'>
                                <input type='checkbox' onChange={e => setTruthfulnessConfirmed((e.target as HTMLInputElement).checked)} checked={truthfulnessConfirmed} name='truthfulness_confirmed' />
                                <span>I confirm that the information I have provided above (including my name, institution, position, country, intended use, and field of work) is truthful, accurate, and complete to the best of my knowledge. I agree to notify BrainFocus if any material information changes during my use of the platform.</span>
                            </label>
                            {otherSelectedButEmpty && (
                                <p class='cl-danger mb-2'>Please specify your field of work before creating the account.</p>
                            )}
                            <CsrfToken />
                            <button type='submit' class='bf-action' disabled={submitDisabled}>Create Account</button>
                        </>
                    )}
                </form>
            </div></div>
        );
    }

    async function submitSignup(e: SubmitEvent) {
        try {
            e.preventDefault();
            setSending(true);
            assertUser(surveyState?.completed, 'Survey is not filled in');
            assertValue(tocAccepted || undefined, 'You must accept our Terms and Conditions');
            assertValue(truthfulnessConfirmed || undefined, 'You must confirm your information is truthful');
            const now = new Date().toISOString();
            const payload: SignupPayload = {
                firstName: assertValue(firstName, 'Missing Firstname'),
                lastName: assertValue(lastName, 'Missing Lastname'),
                email: assertValue(email, 'Missing Email'),
                institution: assertValue(institution, 'Missing Institution'),
                position: assertValue(position, 'Missing Position'),
                country: assertValue(country, 'Missing Country'),
                password: assertValue(password, 'Missing Password'),
                passwordConfirm: assertValue(passwordConfirm, 'Missing Password Confirmation'),
                tocAcceptedAt: now,
                truthfulnessConfirmedAt: now,
                survey: surveyState.answers,
            };
            if (fieldOfWorkOther.selected) {
                const trimmed = fieldOfWorkOther.text.trim();
                assertValue(trimmed || undefined, 'Please specify your field of work');
                payload.fieldOfWorkOther = trimmed;
            }
            const result = await BrainFocusApi.signup(payload);
            setNdaPending(result.ndaPending === true);
            setSignupComplete(true);
        } catch (e) {
            flashError('Cannot submit signup', e);
        } finally {
            setSending(false);
        }
    }
}
